import { type MouseEvent, useEffect, useState } from "react";
import { LuCheck } from "react-icons/lu";
import Button, { type ButtonProps } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { useTranslations } from "@/features/translations/components/translations.tsx";

const POPUP_TIMEOUT = 2_000;

type Props = ButtonProps & { data: string };
export default function CopyToClipboard({ onClick, data, ...props }: Props) {
    const t = useTranslations();

    const [popupOpen, setPopupOpen] = useState<boolean>(false);

    const canCopy = "clipboard" in navigator;

    const handleCopy = (evt: MouseEvent<HTMLButtonElement>) => {
        if (!canCopy) return;

        navigator.clipboard
            .writeText(data)
            .then(() => setPopupOpen(true))
            .catch(console.error);

        onClick?.(evt);
    };

    useEffect(() => {
        if (!popupOpen) return undefined;

        const timeout = setTimeout(() => {
            setPopupOpen(false);
            clearTimeout(timeout);
        }, POPUP_TIMEOUT);

        return () => {
            clearTimeout(timeout);
        };
    }, [popupOpen]);

    if (!canCopy) return null;

    return (
        <Tooltip disableHoverableContent open={popupOpen}>
            <TooltipTrigger>
                <Button {...props} onClick={handleCopy} />
            </TooltipTrigger>
            <TooltipContent className="copy-to-clipboard" sideOffset={8}>
                <LuCheck /> {t("copied")}
            </TooltipContent>
        </Tooltip>
    );
}
